/**
 * Input sanitization utilities to prevent XSS attacks
 */

/**
 * Sanitize HTML content to prevent XSS
 */
export const sanitizeHtml = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Sanitize URL to prevent malicious redirects
 */
export const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    // Only allow http/https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '#';
    }
    return url;
  } catch {
    return '#';
  }
};

/**
 * Sanitize user input for display
 */
export const sanitizeUserInput = (input: unknown): string => {
  if (typeof input !== 'string') {
    return String(input || '');
  }
  
  return input
    .replace(/[<>"']/g, '') // Remove potential HTML tags and quotes
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol (except for our controlled base64 images)
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
    .substring(0, 1000); // Limit length
};

/**
 * Validate and sanitize coordinates
 */
export const sanitizeCoordinates = (lat: number, lng: number): { lat: number; lng: number } | null => {
  const numLat = Number(lat);
  const numLng = Number(lng);
  
  if (isNaN(numLat) || isNaN(numLng)) {
    return null;
  }
  
  if (numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) {
    return null;
  }
  
  return { lat: numLat, lng: numLng };
};

/**
 * Sanitize base64 image data
 */
export const sanitizeBase64Image = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Only allow base64 characters
  const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Pattern.test(input)) {
    return '';
  }
  
  return input;
};

/**
 * Sanitize numeric input
 */
export const sanitizeNumber = (input: unknown, min?: number, max?: number): number | null => {
  const num = Number(input);
  
  if (isNaN(num) || !isFinite(num)) {
    return null;
  }
  
  if (min !== undefined && num < min) {
    return null;
  }
  
  if (max !== undefined && num > max) {
    return null;
  }
  
  return num;
};